/**
 * ChangeRoleModal — pick a new role for a user.
 *
 * The available roles depend on the actor (passed in as allowedRoles): a regular
 * admin sees Admin/User; an HR manager also sees HR Manager. Confirm is disabled
 * until a different role is chosen. Backend enforces the same rules (defense-in-depth).
 */

import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import { ROLE_LABELS } from '../../lib/roles';
import type { Role } from '../../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  userName: string;
  currentRole: Role;
  allowedRoles: Role[];
  onConfirm: (role: Role) => void;
  isLoading?: boolean;
}

export const ChangeRoleModal = ({
  isOpen,
  onClose,
  userName,
  currentRole,
  allowedRoles,
  onConfirm,
  isLoading = false,
}: Props) => {
  const [selected, setSelected] = useState<Role>(currentRole);

  useEffect(() => {
    if (isOpen) setSelected(currentRole);
  }, [isOpen, currentRole]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Change Role" size="sm">
      <div className="space-y-4">
        <p className="text-sm text-meta">
          Change the role for <span className="font-medium text-heading">{userName}</span>.
        </p>
        <Select
          label="Role"
          value={selected}
          onChange={(e) => setSelected(e.target.value as Role)}
          options={allowedRoles.map((r) => ({ value: r, label: ROLE_LABELS[r] }))}
        />
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => onConfirm(selected)}
            isLoading={isLoading}
            disabled={selected === currentRole}
          >
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
};
